import { PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Divider,
  Flex,
  List,
  Popover,
  Table,
  TableColumnsType,
  Tag
} from 'antd'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useStocksServiceGetWriteOffListEndpoint } from 'src/api/queries'
import {
  InternalDocumentResponse,
  InventoryDocumentState,
  Permissions
} from 'src/api/requests'
import { PageBreadcrumbs } from 'src/components'
import { SettlementsFilters } from 'src/features/stock/components/settlements'
import { SelectedItemsState } from 'src/features/stock/types'
import { getSettlementStatus } from 'src/features/stock/utils'
import { useTablePagination, useUser } from 'src/hooks'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'

const VISIBLE_TAG_COUNT = 2

const MapValuesToIds = (data: SelectedItemsState) => {
  const { userData, hasPermission } = useUser()

  const isStockAdmin = hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE)

  return {
    legalEntityIds: !isStockAdmin
      ? [userData?.currentLegalEntityId ?? '']
      : data?.legalEntityIds?.values,
    fromDate: data?.inDate?.values?.[0],
    toDate: data?.inDate?.values?.[1],
    searchTerm: data?.search?.values?.[0],
    states: isStockAdmin
      ? (data?.states?.values as InventoryDocumentState[])
      : ((data?.states?.values as InventoryDocumentState[]) ??
        Object.values(InventoryDocumentState).filter(
          (state) => state !== InventoryDocumentState.NEW
        ))
  }
}

export const StockSettlementsPage = () => {
  const [selectedItems, setSelectedItems] = useState<SelectedItemsState>({})
  const { hasPermission } = useUser()

  const navigate = useNavigate()
  const { t } = useLocales()

  const pagination = useTablePagination()

  const isStockAdmin = hasPermission(Permissions.STOCKS_MENU_ACCESS_ISSUANCE)

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    ...MapValuesToIds(selectedItems)
  }

  const { data, isLoading } =
    useStocksServiceGetWriteOffListEndpoint(queryParams)

  const columns = useMemo(
    (): TableColumnsType<InternalDocumentResponse> => [
      {
        key: 'code',
        title: t('common.invoice_number'),
        render: (_, { code }) => `#${code}`
      },
      {
        key: 'documentDate',
        title: t('stock_applications.request_date'),
        render: (_, { documentDate }) =>
          dayjs(documentDate).format(dayMonthYearBackSlash)
      },
      {
        key: 'legalEntity',
        title: t('stock_administration.juridic_person'),
        dataIndex: ['legalEntity', 'name']
      },
      {
        key: 'products',
        title: t('stock_applications.consumables'),
        render: (_, { items }) => {
          if (!items?.length) return '-'

          const extraCount = items?.length - VISIBLE_TAG_COUNT

          return (
            <Flex>
              <List
                itemLayout="horizontal"
                dataSource={items}
                renderItem={(item, index) =>
                  index < VISIBLE_TAG_COUNT && (
                    <Tag
                      key={item?.id}
                    >{`${item?.name} (${item?.quantity})`}</Tag>
                  )
                }
              />
              <Popover
                content={
                  <Flex vertical gap={4}>
                    {items?.slice(VISIBLE_TAG_COUNT).map((item) => (
                      <Tag
                        key={item?.id}
                      >{`${item?.name} (${item?.quantity})`}</Tag>
                    ))}
                  </Flex>
                }
              >
                {extraCount > 0 && <Tag className="pointer">+{extraCount}</Tag>}
              </Popover>
            </Flex>
          )
        }
      },
      {
        key: 'status',
        title: t('form.status'),
        render: ({ state }) => {
          return (
            <Tag color={getSettlementStatus(t, state).color} key={state}>
              {getSettlementStatus(t, state).text}
            </Tag>
          )
        }
      },
      {
        key: 'details',
        render: ({ id, state }) => {
          const isNew = state === InventoryDocumentState.NEW
          const isRejected = state === InventoryDocumentState.REJECTED

          const route = [
            isStockAdmin && (isNew || isRejected) && paths.settlements.edit(id),
            isStockAdmin &&
              !isNew &&
              !isRejected &&
              paths.settlements.details(id),
            !isStockAdmin && paths.settlements.details(id)
          ].find(Boolean)

          return route ? <Link to={route}>{t('common.details')}</Link> : null
        }
      }
    ],
    [t]
  )

  return (
    <PageBreadcrumbs
      title={t('stock.settlement')}
      items={[
        { title: t('menu.consumable_medicaments') },
        { title: t('stock.settlement') }
      ]}
    >
      <Flex vertical gap={16}>
        <Flex gap={16} className="full-width">
          <SettlementsFilters
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate(paths.settlements.create)}
          >
            {t('stock_write_offs.add_new_write_off')}
          </Button>
        </Flex>

        <Divider />

        <Table
          rowKey="id"
          columns={columns}
          loading={isLoading}
          dataSource={data?.data}
          scroll={{ x: 'max-content' }}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
